// useHeatmap.js - Custom hook for RTLS heatmap rendering
//
// Responsibilities
// ────────────────
// 1. Accept filter values from rtlsFilter.js (via rtlsMap.js handleRtlsFilterApply)
// 2. Resolve object IDs from the live Riot.Api (no extra auth needed)
// 3. Fetch heatmap data via dataReceiver.js → APIM → Azure Function → RTLS API
// 4. Draw Cylinder shapes on the live Riot.Map instance (colour + size = density)
// 5. Optionally draw Text labels on high-density cells
// 6. Clean up all drawn shapes when cleared or re-applied
//
// Data flow
// ─────────
// rtlsFilter.js → rtlsMap.js → useHeatmap.apply()
//   → resolveObjectIds()   [window.Riot.Api - client-side only]
//   → fetchHeatmap()       [dataReceiver.js → APIM → dataServe.js → RTLS API]
//   → draw shapes on Riot.Map instance
//
// Coordinate system
// ─────────────────
// The /heat-map endpoint returns cell_x and cell_y as world-space metre
// coordinates already centred on each cell (e.g. -163.5, 15.5).
// No origin offset or cell-size multiplication is needed - values are passed
// directly to Riot.Map shape constructors.
//
// Confirmed from endpointExampleResponse.txt:
//   columns: ["cell_x", "cell_y", "total"]
//   data:    [[-163.5, 15.5, 1], [-162.5, 15.5, 1], ...]

import { useState, useCallback, useRef } from "react";
import { fetchHeatmap, DataReceiverError } from "../../utils/dataReceiver";

// ─── Colour mapping ───────────────────────────────────────────────────────────
// Maps a normalised density value (0–1) to an RGB colour via a 5-stop gradient:
//   blue(0) → cyan(0.25) → green(0.5) → yellow(0.75) → red(1)

function heatColor(t) {
  const stops = [
    [0, 0, 255], // t = 0.00  blue
    [0, 255, 255], // t = 0.25  cyan
    [0, 255, 0], // t = 0.50  green
    [255, 255, 0], // t = 0.75  yellow
    [255, 0, 0], // t = 1.00  red
  ];
  const scaled = t * (stops.length - 1);
  const lo = Math.floor(scaled);
  const hi = Math.min(lo + 1, stops.length - 1);
  const frac = scaled - lo;
  return stops[lo].map((v, i) => Math.round(v + (stops[hi][i] - v) * frac));
}

// CSS-ready version of the same 5 stops above, exported so a legend can
// render a gradient bar that always matches the actual disc colors —
// changing heatColor() above and this array together keeps them in sync.
export const HEAT_GRADIENT_STOPS_CSS = [
  "rgb(0, 0, 255)", // fewest positions
  "rgb(0, 255, 255)",
  "rgb(0, 255, 0)",
  "rgb(255, 255, 0)",
  "rgb(255, 0, 0)", // most positions
];
export const HEAT_GRADIENT_CSS = `linear-gradient(to right, ${HEAT_GRADIENT_STOPS_CSS.join(", ")})`;

// ─── Shape constants ──────────────────────────────────────────────────────────

const DISC_Z_BOTTOM = 0; // floor plane z-coordinate
const DISC_Z_TOP = 0.1; // minimal height so Cylinder is never zero-volume
const LABEL_Z = 0.6; // label floats just above the disc top
const LABEL_THRESHOLD = 0.6; // normalised density above which labels render
const RADIUS_MIN = 0.5; // metres - sparse cells
const RADIUS_MAX = 2.5; // metres - dense cells
const ALPHA_MIN = 0.2;
const ALPHA_MAX = 0.78;

// ─── Shape removal helper ─────────────────────────────────────────────────────
// Mirrors useSpaghettiMap.js - tries shape.delete() first, then map.remove().

function removeShape(map, shape) {
  try {
    shape.delete();
    return;
  } catch (_) {}
  try {
    if (map && typeof map.remove === "function") map.remove(shape);
  } catch (_) {}
}

// ─── Object ID resolution ─────────────────────────────────────────────────────
// Stays client-side: the backend has no access to the Kinexon WebSocket SDK.
// Resolves a display name string → array of numeric Kinexon object IDs using
// the already-authenticated window.Riot.Api instance.

/**
 * @param {string} objectName - e.g. "Travis Nez" or "12WO010031"
 * @returns {Promise<number[]>}
 */
function resolveObjectIds(objectName) {
  if (!objectName || !objectName.trim()) return Promise.resolve([]);

  try {
    const Riot = window.Riot;
    if (!Riot?.Api?.objectApi) return Promise.resolve([]);

    return Riot.Api.objectApi.getObjects().then(function (allObjects) {
      const q = objectName.trim().toLowerCase();
      return allObjects
        .filter((o) => o.name && o.name.toLowerCase() === q)
        .map((o) => o.id);
    });
  } catch (err) {
    console.warn("[useHeatmap] resolveObjectIds failed:", err);
    return Promise.resolve([]);
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * useHeatmap
 *
 * Full lifecycle: filter input → object ID resolution → APIM fetch →
 * Riot.Map.Cylinder shape drawing → cleanup.
 *
 * @param {React.RefObject} riotMapRef  ref to the live Riot.Map instance
 * @returns {{
 *   isActive:  boolean,
 *   isLoading: boolean,
 *   error:     string | null,
 *   cellCount: number,
 *   minCount:  number,
 *   maxCount:  number,
 *   apply:     (filters, options?) => Promise<void>,
 *   clear:     () => void,
 * }}
 *
 * filters (from rtlsFilter.js onApply):
 *   { beginTs, endTs, labels?: string[], objectNames?: string[] }
 *
 * options:
 *   { locationId = 8, showLabels = false }
 */
export function useHeatmap(riotMapRef) {
  const [isActive, setIsActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cellCount, setCellCount] = useState(0);
  // Density bounds behind the currently-drawn colors — the gradient is
  // normalised per-render (min→max of THIS result set), so a legend needs
  // these to say what blue/red actually mean right now.
  const [minCount, setMinCount] = useState(0);
  const [maxCount, setMaxCount] = useState(0);

  // Tracks all shapes currently drawn so clear() can remove them
  const shapesRef = useRef([]);
  // AbortController for in-flight APIM fetch - cancelled on clear/re-apply
  const abortRef = useRef(null);

  // ── clear ──────────────────────────────────────────────────────────────────
  const clear = useCallback(
    function () {
      // Cancel any in-flight request
      if (abortRef.current) {
        abortRef.current.abort();
        abortRef.current = null;
      }

      const map = riotMapRef.current;
      const shapes = shapesRef.current;
      console.log(
        "[useHeatmap] clear() - removing " + shapes.length + " shapes",
      );
      shapes.forEach((shape) => removeShape(map, shape));
      shapesRef.current = [];
      setIsActive(false);
      setCellCount(0);
      setMinCount(0);
      setMaxCount(0);
      setError(null);
    },
    [riotMapRef],
  );

  // ── apply ──────────────────────────────────────────────────────────────────
  const apply = useCallback(
    function (filters, options) {
      if (!filters) filters = {};
      if (!options) options = {};

      // Validate map is ready BEFORE clearing anything
      const map = riotMapRef.current;
      if (!map) {
        console.warn("[useHeatmap] apply: map not ready");
        return Promise.resolve();
      }

      const Riot = window.Riot;
      if (!Riot?.Map?.Cylinder) {
        setError(
          "RTLS SDK not ready - wait for the map to connect before applying a heatmap.",
        );
        return Promise.resolve();
      }

      // ── Destructure filters from rtlsFilter.js onApply ──────────────────
      // rtlsFilter sends:
      //   { beginTs, endTs, labels?: string[], objectNames?: string[] }
      const beginTs = filters.beginTs;
      const endTs = filters.endTs;
      const objectNames = Array.isArray(filters.objectNames)
        ? filters.objectNames
        : [];
      const labels = Array.isArray(filters.labels) ? filters.labels : [];

      // locationId defaults to 8 - confirmed production location from
      // endpointExampleResponse.txt: ?location_id=8&...
      const locationId = options.locationId != null ? options.locationId : 8;
      const showLabels =
        options.showLabels != null ? options.showLabels : false;

      if (!beginTs || !endTs) {
        setError("A date range is required to generate a heatmap.");
        return Promise.resolve();
      }
      if (beginTs >= endTs) {
        setError("'From' date must be earlier than 'To' date.");
        return Promise.resolve();
      }

      setIsLoading(true);
      setError(null);
      // Clear existing shapes before async work begins
      clear();

      // Fresh AbortController for this fetch lifecycle
      const abortController = new AbortController();
      abortRef.current = abortController;

      // ── Step 1: resolve objectNames → Kinexon object IDs ──────────────────
      // Resolve each name concurrently then flatten - mirrors spaghetti pattern.
      const namesPromise =
        objectNames.length > 0
          ? Promise.all(objectNames.map((name) => resolveObjectIds(name))).then(
              (results) => results.flat(),
            )
          : Promise.resolve([]);

      return namesPromise
        .then(function (objectIds) {
          if (objectNames.length > 0 && objectIds.length === 0) {
            // Names were provided but none resolved - surface a clear error
            // rather than silently fetching the entire facility heatmap.
            throw new Error(
              'No RTLS objects matched "' +
                objectNames.join('", "') +
                '". Verify the object name matches exactly in the RTLS system.',
            );
          }

          if (objectIds.length > 0) {
            console.log(
              "[useHeatmap] Resolved IDs: [" + objectIds.join(", ") + "]",
            );
          }

          // ── Step 2: fetch via dataReceiver → APIM ─────────────────────────
          console.log("[useHeatmap] Fetching heatmap via APIM…", {
            locationId,
            beginTs,
            endTs,
            objectIds,
            labels,
          });

          return fetchHeatmap(
            { locationId, beginTs, endTs, objectIds, labels },
            abortController.signal,
          );
        })
        .then(function (json) {
          // Guard: abort may have fired during the fetch
          if (abortController.signal.aborted) return;

          if (!json.data || json.data.length === 0) {
            setError(
              "No movement data found for this object and time range. " +
                "Try a wider date range or verify the tracker was active.",
            );
            setIsLoading(false);
            return;
          }

          // ── Step 3: normalise counts for colour and radius scaling ─────────
          // Use backend-supplied meta if available (avoids a second pass),
          // otherwise compute locally for resilience.
          const counts = json.data.map((row) => row[2]);
          const maxCount = json.meta?.maxCount ?? Math.max(...counts);
          const minCount = json.meta?.minCount ?? Math.min(...counts);
          const range = maxCount - minCount || 1;

          console.log(
            "[useHeatmap] Drawing " +
              json.data.length +
              " cells. Count range: " +
              minCount +
              "–" +
              maxCount,
          );

          // Re-read map ref AFTER the async fetch in case of re-render -
          // mirrors the same guard used in useSpaghettiMap.js.
          const currentMap = riotMapRef.current;
          if (!currentMap) {
            console.warn(
              "[useHeatmap] map became unavailable during fetch - aborting draw",
            );
            setIsLoading(false);
            return;
          }

          const currentRiot = window.Riot;
          if (!currentRiot?.Map?.Cylinder) {
            setError("RTLS SDK became unavailable during fetch.");
            setIsLoading(false);
            return;
          }

          // ── Step 4: draw one Cylinder disc per cell ────────────────────────
          //
          // Coordinate system note:
          //   cell_x and cell_y from the RTLS API are already world-space metre
          //   coordinates with the cell-centre offset included (e.g. -163.5).
          //   They are passed directly to shape constructors - no conversion.
          //
          // Cylinder API (jsapi.md):
          //   center: [x, y, z]   ← 3-element array required
          //   radius: number
          //   top:    number      ← must differ from bottom to avoid zero volume
          //   bottom: number
          //   color:  [r, g, b, a]
          //
          const newShapes = [];

          json.data.forEach(function (row) {
            const cell_x = row[0];
            const cell_y = row[1];
            const count = row[2];

            const t = (count - minCount) / range;
            const [r, g, b] = heatColor(t);
            const alpha = ALPHA_MIN + t * (ALPHA_MAX - ALPHA_MIN);
            const radius = RADIUS_MIN + t * (RADIUS_MAX - RADIUS_MIN);

            try {
              const disc = new currentRiot.Map.Cylinder({
                center: [cell_x, cell_y, DISC_Z_BOTTOM],
                radius,
                top: DISC_Z_TOP,
                bottom: DISC_Z_BOTTOM,
                color: [r, g, b, alpha],
              });
              currentMap.add(disc);
              newShapes.push(disc);
            } catch (shapeErr) {
              console.warn(
                "[useHeatmap] Failed to draw disc at (" +
                  cell_x +
                  ", " +
                  cell_y +
                  "):",
                shapeErr,
              );
            }

            // Optional count label on high-density cells
            if (showLabels && t >= LABEL_THRESHOLD && currentRiot.Map.Text) {
              try {
                const lbl = new currentRiot.Map.Text({
                  text: String(count),
                  position: [cell_x, cell_y, LABEL_Z],
                  size: 11,
                  color: [255, 255, 255, 0.9],
                  align: "center",
                });
                currentMap.add(lbl);
                newShapes.push(lbl);
              } catch (_) {
                // Label failure is non-critical - disc is already drawn
              }
            }
          });

          // ── Step 5: persist refs and update state ──────────────────────────
          shapesRef.current = newShapes;
          setIsActive(true);
          setCellCount(json.data.length);
          setMinCount(minCount);
          setMaxCount(maxCount);

          console.log(
            "[useHeatmap] Done - " +
              newShapes.length +
              " shapes drawn (" +
              json.data.length +
              " cells).",
          );
        })
        .catch(function (err) {
          // Ignore abort cancellations (user cleared/re-applied mid-fetch)
          if (err.name === "AbortError") return;

          console.error("[useHeatmap] apply error:", err);
          setError(
            err instanceof DataReceiverError
              ? err.message
              : err.message || "Failed to load heatmap data.",
          );
          clear();
        })
        .finally(function () {
          // Only clear loading state if this controller is still the active one
          if (abortRef.current === abortController) {
            setIsLoading(false);
            abortRef.current = null;
          }
        });
    },
    [riotMapRef, clear],
  );

  return {
    isActive,
    isLoading,
    error,
    cellCount,
    minCount,
    maxCount,
    apply,
    clear,
  };
}
